import React from 'react'
import { Icon, Theme, Tooltip } from '@material-ui/core'

import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction'
import { EventApi, EventInput, EventSourceInput } from '@fullcalendar/core'

import AutorenewIcon from '@material-ui/icons/Autorenew'
import EventIcon from '@material-ui/icons/Event'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import CancelIcon from '@material-ui/icons/Cancel'
import PauseCircleOutlineIcon from '@material-ui/icons/PauseCircleOutline'

import {
    LeaseAudit,
    LeaseAuditEvent,
    LeaseAuditEventStatus,
    LeaseAuditSchedule,
    RecurrencePattern,
} from '../../../../../../models/DocumentAudit/types'
import { LeaseAuditStatus } from '../../../../../../models/DocumentAudit/types'
import { Container } from '../../../../../../components'
import { typographyStyle } from '../../../../../../theme/typographyStyle'

type EventType = 'Standalone' | 'Event' | 'MetaSchedule'

interface BaseCalendarEvent extends Omit<EventInput, 'extendedProps'> {
    extendedProps: {
        eventType: EventType
    }
}

interface StandaloneCalendarEvent extends BaseCalendarEvent {
    extendedProps: {
        eventType: 'Standalone'
        object: LeaseAudit
    }
}

interface AuditEventCalendarEvent extends BaseCalendarEvent {
    extendedProps: {
        eventType: 'Event'
        object: LeaseAuditEvent
    }
}

interface MetaScheduleCalendarEvent extends BaseCalendarEvent {
    extendedProps: {
        eventType: 'MetaSchedule'
        object: LeaseAuditSchedule
    }
}

type CalendarEvent =
    | StandaloneCalendarEvent
    | AuditEventCalendarEvent
    | MetaScheduleCalendarEvent

interface Props {
    theme: Theme
    schedules: LeaseAuditSchedule[]
    leaseAudits: LeaseAudit[]
    events: LeaseAuditEvent[]
    onDateClick: (arg: DateClickArg) => void
    onEventClick: (
        eventId: number,
        eventType: EventType,
        xPos: number,
        yPos: number,
    ) => void
}

export const ScheduleCalendar: React.FC<Props> = (props) => {
    const {
        theme,
        schedules,
        leaseAudits,
        events,
        onDateClick,
        onEventClick,
    } = props

    const calendarEvents: CalendarEvent[] = []

    // Create a Map to store schedule_id -> Set of event dates
    const scheduleEventDates = new Map<number, Set<string>>()

    const standAloneEvents: CalendarEvent[] = leaseAudits.map((leaseAudit) => ({
        title: leaseAudit.name,
        start: leaseAudit.created_date,
        end: leaseAudit.created_date,
        extendedProps: {
            eventType: 'Standalone',
            object: leaseAudit,
        },
    }))
    // Organize existing events and build our lookup map in O(n)
    const existingEvents: CalendarEvent[] = events.flatMap((event) => {
        // Initialize Set for this schedule if it doesn't exist
        if (!scheduleEventDates.has(event.lease_audit_schedule.id)) {
            scheduleEventDates.set(event.lease_audit_schedule.id, new Set())
        }
        // Add the event date to the Set
        scheduleEventDates
            .get(event.lease_audit_schedule.id)
            ?.add(event.event_date.split('T')[0])

        return [
            {
                title: event.lease_audit_schedule.name,
                start: event.event_date,
                end: event.event_date,
                groupId: event.id.toString(),
                extendedProps: {
                    eventType: 'Event',
                    object: event,
                },
            },
        ]
    })

    const recurredEvents: CalendarEvent[] = schedules.flatMap((schedule) => {
        const recurrence = schedule.recurrence_pattern
        if (!recurrence) return []

        const recurrenceDates = getRecurrenceDates(
            recurrence,
            schedule.start_date,
        )

        const existingDatesForSchedule =
            scheduleEventDates.get(schedule.id) || new Set()

        // Only include dates that don't have existing events
        return recurrenceDates
            .filter(
                (date) =>
                    !existingDatesForSchedule.has(
                        date.toISOString().split('T')[0],
                    ),
            )
            .map((date) => ({
                title: schedule.name,
                start: date.toISOString(),
                end: date.toISOString(),
                groupId: schedule.id.toString(),
                extendedProps: {
                    eventType: 'MetaSchedule',
                    object: schedule,
                },
            }))
    })

    calendarEvents.push(
        ...recurredEvents,
        ...existingEvents,
        ...standAloneEvents,
    )

    return (
        <FullCalendar
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            height={'100%'}
            dateClick={onDateClick}
            events={calendarEvents}
            eventClick={(info) => {
                const rect = info.el.getBoundingClientRect()
                const event = info.event
                const eventType = event.extendedProps.eventType
                let eventId: number

                if (eventType === 'Event') {
                    eventId = event.extendedProps.object.id
                } else if (eventType === 'MetaSchedule') {
                    eventId = event.extendedProps.object.id
                } else {
                    // Standalone
                    eventId = event.extendedProps.object.id
                }

                onEventClick(eventId, eventType, rect.x, rect.y)
            }}
            eventContent={(eventInfo) =>
                renderEventContent(eventInfo.event, theme)
            }
        />
    )
}

const renderEventContent = (event: EventApi, theme: Theme) => {
    const { extendedProps } = event

    let statusIcon = null
    let secondaryText = ''

    switch (extendedProps.eventType) {
        case 'Standalone': {
            const audit = extendedProps.object as LeaseAudit
            statusIcon = getStatusIcon(extendedProps.eventType, audit.status)
            secondaryText = 'Stand alone'
            break
        }
        case 'Event': {
            const auditEvent = extendedProps.object as LeaseAuditEvent
            secondaryText =
                auditEvent.lease_audit_schedule.recurrence_pattern?.toLowerCase() ??
                ''
            statusIcon = getStatusIcon(
                extendedProps.eventType,
                auditEvent.status,
            )
            break
        }
        case 'MetaSchedule': {
            const schedule = extendedProps.object as LeaseAuditSchedule
            statusIcon = getStatusIcon(extendedProps.eventType, null, event)
            secondaryText = schedule.recurrence_pattern?.toLowerCase() ?? ''
            break
        }
    }

    return (
        <Container>
            <Container
                style={{
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                {statusIcon}
            </Container>
            <div
                style={{
                    ...typographyStyle(theme.typography.body1),
                    display: 'flex',
                    flexDirection: 'column',
                    marginLeft: theme.spacing(1),
                }}
            >
                <span style={{ color: theme.palette.text.primary }}>
                    {event.title}
                </span>
                <span style={{ color: theme.palette.text.secondary }}>
                    {secondaryText}
                </span>
            </div>
        </Container>
    )
}

const getStatusIcon = (
    eventType: EventType,
    status: LeaseAuditEventStatus | LeaseAuditStatus | null,
    event?: EventApi,
) => {
    if (eventType === 'MetaSchedule') {
        const schedule = event?.extendedProps.object as LeaseAuditSchedule
        if (schedule?.status === 'PAUSED') {
            return (
                <Tooltip title="Paused">
                    <PauseCircleOutlineIcon style={{ color: '#757575' }} />
                </Tooltip>
            )
        }
        return (
            <Tooltip title="Scheduled">
                <EventIcon style={{ color: '#1976D2' }} />
            </Tooltip>
        )
    }

    if (eventType === 'Event') {
        switch (status as LeaseAuditEventStatus) {
            case 'SCHEDULED':
                return (
                    <Tooltip title="Scheduled">
                        <EventIcon style={{ color: '#1976D2' }} />
                    </Tooltip>
                )
            case 'IN_PROGRESS':
                return (
                    <Tooltip title="In Progress">
                        <AutorenewIcon style={{ color: '#F57C00' }} />
                    </Tooltip>
                )
            case 'COMPLETED':
                return (
                    <Tooltip title="Completed">
                        <CheckCircleIcon style={{ color: '#2E7D32' }} />
                    </Tooltip>
                )
            case 'CANCELLED':
                return (
                    <Tooltip title="Cancelled">
                        <CancelIcon style={{ color: '#D32F2F' }} />
                    </Tooltip>
                )
            default:
                return (
                    <Tooltip title="Scheduled">
                        <EventIcon style={{ color: '#1976D2' }} />
                    </Tooltip>
                )
        }
    }

    if (eventType === 'Standalone') {
        switch (status as LeaseAuditStatus) {
            case 'REQUESTED':
            case 'SETUP':
            case 'WAITING_FOR_APPROVAL':
                return (
                    <Tooltip title={status!.toLowerCase()}>
                        <EventIcon style={{ color: '#1976D2' }} />
                    </Tooltip>
                )
            case 'RUNNING_PIPELINE':
                return (
                    <Tooltip title="Running Pipeline">
                        <AutorenewIcon style={{ color: '#F57C00' }} />
                    </Tooltip>
                )
            case 'COMPLETED':
            case 'APPROVED':
                return (
                    <Tooltip title={status!.toLowerCase()}>
                        <CheckCircleIcon style={{ color: '#2E7D32' }} />
                    </Tooltip>
                )
            case 'REJECTED':
                return (
                    <Tooltip title="Rejected">
                        <CancelIcon style={{ color: '#D32F2F' }} />
                    </Tooltip>
                )
            default:
                return (
                    <Tooltip title="Requested">
                        <EventIcon style={{ color: '#1976D2' }} />
                    </Tooltip>
                )
        }
    }

    return null
}

const getRecurrenceDates = (
    recurrence: RecurrencePattern,
    startDate: string,
) => {
    const recurrenceDates: Date[] = []
    const today = new Date()
    const maxDates = 100 // Maximum number of future dates to generate
    let currentDate = new Date(startDate)

    while (recurrenceDates.length < maxDates) {
        switch (recurrence) {
            case 'DAILY':
                currentDate = new Date(
                    currentDate.setDate(currentDate.getDate() + 1),
                )
                break
            case 'WEEKLY':
                currentDate = new Date(
                    currentDate.setDate(currentDate.getDate() + 7),
                )
                break
            case 'BIWEEKLY':
                currentDate = new Date(
                    currentDate.setDate(currentDate.getDate() + 14),
                )
                break
            case 'MONTHLY':
                currentDate = new Date(
                    currentDate.setMonth(currentDate.getMonth() + 1),
                )
                break
            case 'QUARTERLY':
                currentDate = new Date(
                    currentDate.setMonth(currentDate.getMonth() + 3),
                )
                break
            case 'ANNUALLY':
                currentDate = new Date(
                    currentDate.setFullYear(currentDate.getFullYear() + 1),
                )
                break
            default:
                return recurrenceDates
        }

        // Only add dates that are after today
        if (currentDate >= today) {
            recurrenceDates.push(new Date(currentDate))
        }
    }

    return recurrenceDates
}
